class ReviewsDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#ReviewsDrawer-Overlay').addEventListener('click', this.close.bind(this));
    
    this.setReviewsBadgeAccessibility();
    this.observeBadgeChanges();
  }

  observeBadgeChanges() {
    const observer = new MutationObserver((mutations) => {
      let shouldReinitialize = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.classList && (node.classList.contains('jdgm-preview-badge') || 
                node.classList.contains('jdgm-prev-badge__text') ||
                node.querySelector('.jdgm-preview-badge') || 
                node.querySelector('.jdgm-prev-badge__text'))) {
              shouldReinitialize = true;
            }
          }
        });
      });
      if (shouldReinitialize) {
        this.setReviewsBadgeAccessibility();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  setReviewsBadgeAccessibility() {
    // Find all Judge.me badge elements including the specific text element
    const selectors = [
      '.jdgm-preview-badge',
      '.jdgm-prev-badge__text',
      '.jdgm-widget.jdgm-preview-badge',
      '.jdgm-prev-badge'
    ];
    
    selectors.forEach(selector => {
      const badges = document.querySelectorAll(selector);
      badges.forEach((badge) => {
        // Skip if already initialized
        if (badge.dataset.reviewsDrawerInitialized === 'true') {
          return;
        }
        
        badge.dataset.reviewsDrawerInitialized = 'true';
        badge.style.cursor = 'pointer';
        badge.setAttribute('role', 'button');
        badge.setAttribute('aria-haspopup', 'dialog');
        badge.setAttribute('tabindex', '0');
        
        badge.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.open(badge);
        });
        
        badge.addEventListener('keydown', (event) => {
          if (event.code.toUpperCase() === 'SPACE' || event.code.toUpperCase() === 'ENTER') {
            event.preventDefault();
            this.open(badge);
          }
        });
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    
    // Get product information from the page
    this.updateProductReviews();
    
    // Initialize filter buttons
    this.initializeFilters();
    
    // Set "All" button as active by default
    this.setDefaultFilterState();
    
    // Initialize write review button
    this.initializeWriteReview();
    
    // Watch for Judge.me reviews being loaded
    this.watchForReviewsLoaded();
    
    // Add animation with slight delay to ensure it triggers
    setTimeout(() => {
      this.classList.add('animate', 'active');
    }, 10);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('ReviewsDrawer');
        const focusElement = this.querySelector('.reviews-drawer__inner') || this.querySelector('.reviews-drawer__close');
        if (typeof trapFocus === 'function') {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  watchForReviewsLoaded() {
    const reviewsContainer = this.querySelector('#judgeme_product_reviews');
    if (!reviewsContainer) return;

    let initialLoadTriggered = false;
    let debounceTimer = null;

    // Create observer to watch for Judge.me reviews being added
    const observer = new MutationObserver((mutations) => {
      let reviewsAdded = false;
      let histogramAdded = false;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Check for review elements
            if (node.classList?.contains('jdgm-rev') ||
                node.classList?.contains('jdgm-rev-widg') ||
                node.querySelector?.('.jdgm-rev')) {
              reviewsAdded = true;
            }
            
            // Check for histogram data
            if (node.classList?.contains('jdgm-histogram') ||
                node.querySelector?.('.jdgm-histogram_row[data-frequency]')) {
              histogramAdded = true;
            }
          }
        });
      });

      if (reviewsAdded || histogramAdded) {
        // Debounce the response to prevent excessive calls
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          // Update filter counts when histogram data is available
          this.updateFilterCounts();
          
          // Reviews detected, update counts
              if (!initialLoadTriggered && reviewsAdded) {
                initialLoadTriggered = true;
              }
          
          // Update counts again after a delay
          setTimeout(() => this.updateFilterCounts(), 1000);
        }, 300); // Debounce delay
      }
    });

    observer.observe(reviewsContainer, {
      childList: true,
      subtree: true
    });

    // Store observer so we can disconnect it later
    this._reviewsObserver = observer;
  }

  updateProductReviews() {
    const reviewsWidget = this.querySelector('#judgeme_product_reviews');
    if (!reviewsWidget) return;

    // Get product data from existing Judge.me widgets
    const existingWidget = document.querySelector('.jdgm-widget[data-id]');
    if (existingWidget) {
      const productId = existingWidget.getAttribute('data-id');
      const productTitle = existingWidget.getAttribute('data-product-title');
      
      if (productId) reviewsWidget.setAttribute('data-id', productId);
      if (productTitle) reviewsWidget.setAttribute('data-product-title', productTitle);
    }

    // Load Judge.me reviews
    if (typeof jdgm !== 'undefined' && typeof jdgm.loadReviewWidget === 'function') {
      jdgm.loadReviewWidget();
    }
    
    this.updateFilterCounts();
    
    // Update counts multiple times to catch Judge.me data
    setTimeout(() => this.updateFilterCounts(), 1000);
    setTimeout(() => this.updateFilterCounts(), 3000);
    setTimeout(() => this.updateFilterCounts(), 5000);
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
    
    // Disconnect the reviews observer
    if (this._reviewsObserver) {
      this._reviewsObserver.disconnect();
      this._reviewsObserver = null;
    }
    
    // Clear any pending timeouts
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
    
    // Reset filters to "5 Stars" when closing
        const allButton = this.querySelector('.reviews-drawer__filter-btn[data-rating="all"]');
        if (allButton) {
          const filterBtns = this.querySelectorAll('.reviews-drawer__filter-btn');
          filterBtns.forEach(b => b.classList.remove('reviews-drawer__filter-btn--active'));
          allButton.classList.add('reviews-drawer__filter-btn--active');
          this.filterReviews('all');
        }
    
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initializeFilters() {
    // Wait for Judge.me reviews to load before initializing filters
    // Try multiple times in case reviews load slowly
    const tryInitialize = (attempt = 0) => {
      const allReviews = this.querySelectorAll('.jdgm-rev, .jdgm-rev-widg__reviews > div[data-review-id]');
      
      if (allReviews.length > 0 || attempt >= 5) {
        // Reviews found or max attempts reached
        this.updateFilterCounts();
        
        const filterBtns = this.querySelectorAll('.reviews-drawer__filter-btn');
        
        filterBtns.forEach(btn => {
          // Remove any existing listeners by cloning the button
          const newBtn = btn.cloneNode(true);
          btn.parentNode.replaceChild(newBtn, btn);
          
          newBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const allFilterBtns = this.querySelectorAll('.reviews-drawer__filter-btn');
            
            // Remove active class from all buttons
            allFilterBtns.forEach(b => b.classList.remove('reviews-drawer__filter-btn--active'));
            
            // Add active class to clicked button
            newBtn.classList.add('reviews-drawer__filter-btn--active');
            
            // Get the rating filter value
            const rating = newBtn.getAttribute('data-rating');
            
            // Filter Judge.me reviews
            this.filterReviews(rating);
          });
        });
      } else {
        // Retry after delay
        setTimeout(() => tryInitialize(attempt + 1), 500);
      }
    };
    
    tryInitialize(0);
  }

  updateFilterCounts() {
    const ratingCounts = { all: 0, 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    let totalRating = 0;
    let totalReviews = 0;
    
    // Try multiple approaches to get histogram data
    let histogramRows = this.querySelectorAll('.jdgm-histogram_row[data-frequency]');
    
    // If not found in drawer, look in entire document
    if (histogramRows.length === 0) {
      histogramRows = document.querySelectorAll('.jdgm-histogram_row[data-frequency]');
    }
    
    // Try alternative selectors
    if (histogramRows.length === 0) {
      histogramRows = document.querySelectorAll('[data-rating][data-frequency], .jdgm-histogram [data-rating]');
    }
    
    // Get counts from histogram data
    histogramRows.forEach(row => {
      const rating = parseInt(row.getAttribute('data-rating'));
      const frequency = parseInt(row.getAttribute('data-frequency'));
      
      if (rating >= 1 && rating <= 5 && frequency > 0) {
        ratingCounts[rating] = frequency;
        totalReviews += frequency;
        totalRating += rating * frequency;
      }
    });
    
    // If still no histogram data, try to get from Judge.me summary text
    if (totalReviews === 0) {
      // Look for "Based on X reviews" text
      const summaryText = document.querySelector('.jdgm-rev-widg__summary-text, .jdgm-summary-text');
      if (summaryText) {
        const text = summaryText.textContent;
        const match = text.match(/Based on (\d+) reviews?/i);
        if (match) {
          totalReviews = parseInt(match[1]);
        }
      }
    }
    
    // Fallback to counting individual reviews
    if (totalReviews === 0) {
      const allReviews = this.querySelectorAll('.jdgm-rev, .jdgm-rev-widg__reviews > div[data-review-id]');
      totalReviews = allReviews.length;
      
      allReviews.forEach(review => {
        const score = this.getReviewRating(review);
        if (score >= 1 && score <= 5) {
          ratingCounts[score]++;
          totalRating += score;
        }
      });
    }
    
    // Set the "All" count to total reviews
    ratingCounts.all = totalReviews;
    
    // Update count badges
    Object.keys(ratingCounts).forEach(rating => {
      const countElement = this.querySelector(`[data-filter-count="${rating}"]`);
      if (countElement) {
        countElement.textContent = `(${ratingCounts[rating]})`;
      }
    });
  }

  filterReviews(rating) {
    // Use requestAnimationFrame to batch DOM operations and prevent forced reflow
    requestAnimationFrame(() => {
      // Handle "All" filter - show all reviews
      if (rating === 'all') {
        this.showAllReviews();
        this.updateFilterButtonStates('all');
        return;
      }
      
      const targetRating = parseInt(rating);
      
      // Clear any existing Judge.me filter selections first
      this.clearJudgeMeFilters();
      
      // Use Judge.me's native histogram filtering
      const histogramRow = document.querySelector(`.jdgm-histogram__row[data-rating="${targetRating}"]`);
      if (histogramRow) {
        // Click the histogram row to trigger Judge.me's native filtering
        histogramRow.click();
        
        // Update the visual state of our custom buttons
        this.updateFilterButtonStates(targetRating);
        return;
      }
      
      // Fallback: try alternative selector
      const altHistogramRow = document.querySelector(`.jdgm-histogram_row[data-rating="${targetRating}"]`);
      if (altHistogramRow) {
        altHistogramRow.click();
        
        // Update the visual state of our custom buttons
        this.updateFilterButtonStates(targetRating);
        return;
      }
      
      // Fallback to manual filtering if histogram not found
      const allReviews = this.querySelectorAll('.jdgm-rev, .jdgm-rev-widg__reviews > div[data-review-id]');
      
      let matchingReviews = 0;
      allReviews.forEach((review, index) => {
        const reviewRating = this.getReviewRating(review);
        
        if (reviewRating === targetRating) {
          review.style.display = '';
          matchingReviews++;
        } else {
          review.style.display = 'none';
        }
      });
      
      // Check if no reviews match the filter
      const visibleReviews = Array.from(allReviews).filter(review => review.style.display !== 'none');
      let noResultsMsg = this.querySelector('.reviews-drawer__no-results');
      
      if (visibleReviews.length === 0 && !noResultsMsg) {
        const reviewsContainer = this.querySelector('#judgeme_product_reviews');
        if (reviewsContainer) {
          const msg = document.createElement('div');
          msg.className = 'reviews-drawer__no-results';
          msg.innerHTML = `<p>No reviews found for this rating.</p><p>Try selecting a different star rating.</p>`;
          reviewsContainer.appendChild(msg);
        }
      } else if (visibleReviews.length > 0 && noResultsMsg) {
        noResultsMsg.remove();
      }
      
      // Update the visual state of our custom buttons
      this.updateFilterButtonStates(targetRating);
    });
  }

  showAllReviews() {
    // Try to use Judge.me's native clear filter
    const clearFilterBtn = document.querySelector('.jdgm-histogram__clear-filter');
    if (clearFilterBtn) {
      clearFilterBtn.click();
      
      // Remove any no results message
      const noResultsMsg = this.querySelector('.reviews-drawer__no-results');
      if (noResultsMsg) {
        noResultsMsg.remove();
      }
      
      return;
    }
    
    // Fallback: try alternative clear filter selectors
    const altClearFilter = document.querySelector('.jdgm-clear-filter, .jdgm-histogram-clear, [class*="clear-filter"]');
    if (altClearFilter) {
      altClearFilter.click();
      
      // Remove any no results message
      const noResultsMsg = this.querySelector('.reviews-drawer__no-results');
      if (noResultsMsg) {
        noResultsMsg.remove();
      }
      
      return;
    }
    
    // Fallback: manual approach
    const allReviews = this.querySelectorAll('.jdgm-rev, .jdgm-rev-widg__reviews > div[data-review-id]');
    allReviews.forEach(review => {
      review.style.display = '';
    });
    
    // Remove any no results message
    const noResultsMsg = this.querySelector('.reviews-drawer__no-results');
    if (noResultsMsg) {
      noResultsMsg.remove();
    }
    
    // Clear Judge.me filter selections
    this.clearJudgeMeFilters();
    
  }

  clearJudgeMeFilters() {
    // Remove selected state from all histogram rows
    const selectedRows = document.querySelectorAll('.jdgm-histogram_row--selected');
    selectedRows.forEach(row => {
      row.classList.remove('jdgm-histogram_row--selected');
    });
    
    // Show all reviews by default
    const allReviews = this.querySelectorAll('.jdgm-rev, .jdgm-rev-widg__reviews > div[data-review-id]');
    allReviews.forEach(review => {
      review.style.display = '';
    });
  }

  setDefaultFilterState() {
    // Set "All" button as active by default
    const filterBtns = this.querySelectorAll('.reviews-drawer__filter-btn');
    filterBtns.forEach(btn => {
      btn.classList.remove('reviews-drawer__filter-btn--active');
    });
    
    const allButton = this.querySelector('.reviews-drawer__filter-btn[data-rating="all"]');
    if (allButton) {
      allButton.classList.add('reviews-drawer__filter-btn--active');
    }
  }

  updateFilterButtonStates(activeRating) {
    // Update our custom filter button states
    const filterBtns = this.querySelectorAll('.reviews-drawer__filter-btn');
    filterBtns.forEach(btn => {
      const btnRating = btn.getAttribute('data-rating');
      
      // Handle both numeric ratings and 'all' string
      if (btnRating === activeRating || 
          (btnRating === 'all' && activeRating === 'all') ||
          (btnRating === String(activeRating))) {
        btn.classList.add('reviews-drawer__filter-btn--active');
      } else {
        btn.classList.remove('reviews-drawer__filter-btn--active');
      }
    });
  }




  initializeWriteReview() {
    const writeBtn = this.querySelector('.reviews-drawer__write-btn');
    if (!writeBtn) return;

    writeBtn.addEventListener('click', () => {
      const reviewLink = document.querySelector('.jdgm-write-rev-link');
      if (reviewLink) {
        reviewLink.click();
        
        // Wait for form to appear, then scroll to it
        setTimeout(() => {
          const formWrapper = document.querySelector('.jdgm-form-wrapper');
          if (formWrapper) {
            formWrapper.scrollIntoView({ behavior: 'smooth' });
          }
        }, 500);
      }
    });
  }

}

customElements.define('reviews-drawer', ReviewsDrawer);

// Re-initialize after Judge.me loads
if (typeof jdgm !== 'undefined') {
  document.addEventListener('judgeme:loaded', function() {
    const reviewsDrawer = document.querySelector('reviews-drawer');
    if (reviewsDrawer) {
      reviewsDrawer.setReviewsBadgeAccessibility();
    }
  });}

